/**
 * The chat protocol.
 *
 * One module, imported by both ends of the wire: the Cloudflare Worker that
 * hosts a room and the tab that talks to it. Everything either side has to
 * agree on lives here — what a room is called, what a message looks like,
 * how long a name or a line may be, how fast one person may post — so that a
 * disagreement is a compile error rather than a message silently dropped.
 *
 * Rooms. There are three kinds, and all of them are just a string the Worker
 * turns into one Durable Object:
 *
 *   "1" … "99"               numbered rooms. Join by typing the number, the
 *                            way a class or a cohort shares a code. Room 1 is
 *                            where everyone starts.
 *   "level-0" … "level-999"  one room per level of the general track, so a
 *                            learner stuck on Level 10 can find the people
 *                            who are on Level 10 too.
 *   "level-<track>-<n>"      the same, for a level on a specialization track.
 *   "team-<id>"              one team's own room (docs/teams.md).
 *
 * The track in the second and third lines is the thing a room id used to be
 * missing, and it was missing because there was only ever one course. A level
 * number is a position *within a track* (docs/curriculum.md §5), so once Act
 * III branches there is a level 0 on backend and a level 0 on frontend, and a
 * room keyed on the bare number would have put both of them — and everybody
 * on general's level 0 — in the same conversation. The general track keeps the
 * unqualified spelling it has always had, so every room a learner has ever
 * been in, linked to or stored a preference for is still the same room.
 *
 * The third one is the first room in this product with a door on it, and the
 * distinction it introduces is worth stating here rather than only where it is
 * enforced. For the first two, "is this a room" and "may I go there" are the
 * same question, and `parseRoomId` answers both: anyone who can type the name
 * can walk in. For a team room they are different questions, and **this module
 * answers only the first.** The second is answered in the Worker at the socket
 * upgrade, against `team_members` in D1, which is where the session cookie and
 * the database are; the Durable Object holding the messages is never told that
 * teams exist and keeps the same history, pins and ban check it always had.
 *
 * Wire. JSON text frames over one WebSocket per room. The first frame from
 * the client must be `hello`; until it arrives the server answers nothing
 * but `err`. The literal frame "ping" is answered "pong" by the platform
 * without waking the room (Durable Object auto-response), which is how a
 * quiet tab stays connected for free.
 *
 * Identity. A signed-in learner is decided by the Worker — it reads the
 * session cookie on the upgrade and tells the room who this is — and the
 * room marks them `verified` (see Member). Where the browser decides
 * instead, it is a random id it keeps, hashed into a tag, and a name it
 * typed. The frames are the same either way; the two extra fields are
 * optional.
 *
 * Which of the two a room uses is the room's to decide, not the wire's, and
 * it turns on one thing: whether signing in is possible at all there. Where
 * it is, posting is for signed-in people and a name on the wire is ignored
 * (worker/room.ts, `gated`). Where it is not — a dev server with no
 * `.dev.vars`, a deployment that never configured Google — nothing is gated
 * and a typed name is who you are, exactly as it always was. Both are real
 * configurations and the protocol describes both; neither is a mode a client
 * selects.
 *
 * Listening. A `hello` with no name is a listener: the socket receives
 * everything the room sends, appears in nobody's member list, and may not
 * post. It exists so that a tab can carry the conversation with it from the
 * first paint — a learner who has never opened the drawer still learns that
 * there is one, because the unread count is honest before they ever click.
 * It is also what everyone signed out is in a gated room, which is the same
 * bet made larger: someone who can read the room can see what an account
 * would be for, and someone shown a wall cannot. Ungated, a listener becomes
 * a member the moment it sends a `me` with a name, and the room answers that
 * with `you`; nothing reconnects. Gated, the way in is the cookie, so it is
 * a fresh socket rather than a promotion.
 *
 * Faces and emoji. Everyone in a room has a face: one of a fixed set the
 * browser may choose from, or the picture on a signed-in account, or — for
 * anyone who has never picked — one derived from their tag, so a room has
 * faces in it without anybody being asked. The set, the palette people type
 * from, and the rule that a browser may name a face and never a URL are all
 * in ./emoji.ts, re-exported below.
 *
 * Operators. The other thing a face can be is the learner's own figure: nine
 * integers naming nine parts, drawn 16×16 on the client (./avatar.ts, and the
 * renderer in web/src/lib/avatar.ts). It travels in the same field as a face
 * and through the same gate — a browser may claim a face or a seed, and never
 * a URL, because a seed is digits and a URL is somebody else's server. Which
 * of the three a member is wearing is `isFace`, `isSeed`, or neither.
 *
 * Answering somebody. For a long time a room could carry lines and nothing
 * else, which is enough for a channel and not enough for a place: ten people
 * talking produced ten monologues, because nothing in the protocol could
 * address one person or point at one thing that had been said. Three frames
 * fix that and they are deliberately the three cheapest ones.
 *
 *   react     an emoji on a message. The smallest act of joining a room
 *             there is, and the one worth making free: it is rate-limited
 *             separately and generously, because a reaction costs a reader
 *             nothing and is how a quiet room stops looking empty.
 *   say +     a reply quotes what it answers, and the quote is copied onto
 *   replyTo   the reply when it is sent rather than looked up when it is
 *             read — a room keeps `LIMITS.keep` messages and a reply
 *             routinely outlives its parent.
 *   mentions  tags, never names, because two people called sam are one name
 *             and two tags and a mention has to reach exactly one of them.
 *
 * Outliving the scroll. A room deletes everything past `LIMITS.keep`, which
 * is right for chatter and wrong for the one answer that was worth the
 * afternoon. `pin` holds a message out of that window, `search` looks through
 * what is still there, and `context` returns a page centred on one message so
 * that a link into a conversation arrives with the conversation around it.
 *
 * The world posting into the room. `event` is how something that happened in
 * the game shows up where the people are — so far one kind, a cleared level.
 * It rides this socket rather than arriving by an HTTP call from the game,
 * because the game loop never touches the Worker and that line is drawn on
 * purpose (docs/architecture.md). It is accepted from a signed-in socket only:
 * what makes it worth showing is that the server can vouch for the name.
 *
 * Moderation. A room had a rate limit and a purge token, which between them
 * could slow one person down or erase everybody. `report` tells whoever runs
 * the deployment about a message, `hidden` is how a removed one still leaves
 * a visible gap, and `banned` is the frame a socket is closed with when its
 * tag may not be here. Blocking somebody is not on the wire at all: it is a
 * preference in one browser, because what one reader does not want to see is
 * nobody else's business and certainly not the server's.
 *
 * Nothing here is HTML. Text is text; the tab renders it as text.
 */
/**
 * The faces and the palette. Re-exported rather than defined here so that one
 * import path is still the whole protocol, and a table of three hundred
 * characters is not in the middle of the rules.
 *
 * `custom.js` is the roster of the emoji this product drew for itself, which
 * travel as their own shortcode rather than as a character. It is part of the
 * protocol in the same sense the face list is: both ends have to agree on what
 * the string means, even though only the tab has the picture.
 */
import { EMOJI, cleanFace } from './emoji.js';
import { CUSTOM_BY_CODE } from './custom.js';
import { cleanSeed } from './avatar.js';
export * from './emoji.js';
export * from './custom.js';
export * from './avatar.js';
export const PROTOCOL_VERSION = 2;
/** The keepalive frames. Sent as bare strings, not JSON. */
export const PING = 'ping';
export const PONG = 'pong';
export const LIMITS = {
    /** Display names, in characters after trimming. */
    name: { min: 2, max: 20 },
    /** One message, in characters after trimming. */
    text: 500,
    /** Messages a room sends on connect, and per `earlier` page. */
    page: 50,
    /** Messages a room keeps. Older ones are deleted as new ones arrive. */
    keep: 1000,
    /** Posting: at most `burst` messages in any `windowMs` window, per socket. */
    rate: { burst: 5, windowMs: 10_000 },
    /** Numbered rooms. */
    room: { min: 1, max: 99 },
    /**
     * Level rooms: level-0 through level-<levelMax>, on every track.
     *
     * Three digits, and the number is chosen to agree with the two other places
     * a level number is read rather than to be generous on its own. A mission id
     * is `l<NN>-<slug>` and `missionLevel` (web/src/lib/chat.ts) reads the digits
     * out of it; the analytics ceiling in worker/events.ts bounds the same
     * number on its way into a column. All three were two digits, all three
     * stopped at 99, and Act II ends at exactly 99 (docs/curriculum.md §2) — so
     * the first level past the end of the plan would have been playable, would
     * not have had a room, would not have been announced in one, and would not
     * have been counted. Making the three ranges the same range is what stops
     * the next one of those from being found one at a time.
     */
    levelMax: 999,
    /** Open sockets one room will hold. Beyond it the room refuses politely. */
    sockets: 200,
    /**
     * Rooms one tab holds a socket to at once: the room it calls home and the
     * room of the level it is standing in. Two, and the second is earned —
     * only someone who can actually post opens one (web/src/state/useChat.ts).
     * Room 1 holds every idle tab in the product and `sockets` is 200, so a
     * second socket for people who will never speak would spend the budget on
     * the wrong half of the audience.
     */
    open: 2,
    /**
     * Reactions. `perPerson` is how many distinct emoji one person may put on
     * one message — enough to agree and joke, not enough to write a sentence
     * in faces — and `perMessage` is how many distinct buckets a message may
     * grow, which is the real cap: it bounds the row the tab has to draw.
     */
    reaction: { perMessage: 12, perPerson: 4 },
    /** Tags one message may address. Past this it is an announcement, not a reply. */
    mention: 8,
    /**
     * Pinned messages a room keeps. They are exempt from the `keep` window —
     * that is the whole point of pinning — so this is the number that bounds
     * how much history one room can hold open for ever.
     */
    pin: 20,
    /** Searching a room: the query's length, and how many rows come back. */
    search: { min: 2, max: 64, results: 50 },
    /** Characters of a quoted reply carried on the wire beside the reply itself. */
    snippet: 140,
    /** Mentions the inbox hands back in one request. */
    inbox: 50,
    /**
     * Teams: a named few people with a room of their own.
     *
     * `size` is six, and it is the number that decides what a team *is*. Six is
     * a group where everybody has said something; past about eight it becomes an
     * audience with a couple of speakers, which is what the numbered rooms are
     * already for and are better at. It also bounds the thing worth bounding —
     * a private room is the one room the operator cannot moderate by reading it
     * in passing, so the smaller it is, the less there is to go wrong in it
     * unseen.
     *
     * `perLearner` is three, because a learner plausibly has a class, a project
     * and a couple of friends, and does not plausibly have twelve. It is the
     * cap that stops a team list becoming a feed.
     *
     * `invites` is how many invitations one team may have outstanding at once.
     * Without it "invite" is a way to put a row in an arbitrary number of other
     * people's inboxes, which is the shape of every invitation spam there has
     * ever been.
     */
    team: { size: 6, perLearner: 3, invites: 20, name: { min: 2, max: 32 } },
};
export const DEFAULT_ROOM = '1';
/**
 * The track every level was on before there were tracks, and the one whose
 * rooms are spelled without it.
 *
 * It is a constant rather than a literal because it is load-bearing in three
 * different ways below — it is the default {@link levelRoom} builds for, the
 * answer {@link roomTrack} gives for an unqualified id, and the one word a
 * qualified id may *not* carry — and those three have to be the same word or
 * a room acquires a second id.
 */
export const GENERAL_TRACK = 'general';
const NUMBERED = /^[1-9][0-9]?$/;
/**
 * A level room, on the general track or on a named one.
 *
 * The track segment is optional, is letters only, and can therefore never be
 * confused with the level that follows it: the level group is digits and is
 * anchored at the end, so `level-7` has no track and `level-backend-7` has
 * exactly one. Letters only is also why a track whose name a future catalogue
 * spells with a digit or an underscore simply has no room rather than a
 * surprising one — see {@link levelRoom}, which fails closed on it.
 *
 * No leading zeros, as before: `level-01` is not a second spelling of
 * `level-1`, because two spellings of one room are two Durable Objects and
 * two halves of one conversation.
 */
const LEVEL = /^level-(?:([a-z]{2,16})-)?(0|[1-9][0-9]{0,2})$/;
/**
 * What the id of a room with a door is made of: 22 characters of base64url,
 * which is `btoa` over the 16 random bytes `crypto.getRandomValues` gives.
 *
 * A fragment rather than four regular expressions, because it was four. Both
 * private families spell it inside their own pattern and both `cleanTeamId`
 * and `cleanSupportId` spelled it again to check the bare half — and nothing
 * made the four move together. That is the failure `http.ts` describes for
 * its own eleven copies: not that any one of them was wrong, but that the
 * next person to add a family would have written a fifth, or not quite. The
 * consequence here is worse than a stray header, because the two ends
 * disagreeing means a route accepts an id that the socket then refuses.
 *
 * Unguessable, and deliberately so even though guessing one buys nothing:
 * the Worker checks at the upgrade and an outsider holding the exact id is
 * refused like anyone else. Being unguessable is the belt; the door is the
 * braces.
 */
const ID22 = '[A-Za-z0-9_-]{22}';
/** That id on its own, as a route or a stored preference carries it. */
const BARE_ID22 = new RegExp(`^${ID22}$`);
/** A team's own room. */
const TEAM = new RegExp(`^team-${ID22}$`);
/**
 * A support conversation's room: one learner, and whoever works here.
 *
 * The same shape as a team's, from the same source, for the same reason — and
 * the sameness is the point of the whole feature rather than a shortcut. A
 * question for support is a conversation between people, which is a thing this
 * product already has, so making it a room rather than a second messaging
 * system means history, editing, pictures, reporting, hiding, purging and
 * hibernation all work in it on the day it ships and none of them had to be
 * written twice.
 *
 * What it does not share with a team is how many doors it has. A team room
 * admits its roster; this one admits two unrelated parties — the learner it
 * belongs to, and any account on the deployment's support roster — and that
 * check lives in the Worker at the upgrade, like the other one.
 */
const SUPPORT = new RegExp(`^support-${ID22}$`);
/**
 * The room id in a URL or a stored preference, or null if it is not one.
 *
 * **Parsing a team room here says nothing about being allowed into it.** That
 * is the one thing about this function that changed when teams arrived, and
 * it is worth being loud about: the numbered and level rooms are open to
 * anyone who can type their name, so for twenty months "is this a room" and
 * "may I go there" were the same question and this function answered both. A
 * team room is the first room with a door. The door is in the Worker, at the
 * socket upgrade, where the session cookie and the database are
 * (`worker/index.ts`); this function only says the string is well formed.
 */
export function parseRoomId(raw) {
    if (typeof raw !== 'string')
        return null;
    if (NUMBERED.test(raw)) {
        const n = Number(raw);
        return n >= LIMITS.room.min && n <= LIMITS.room.max ? raw : null;
    }
    const m = LEVEL.exec(raw);
    if (m) {
        // `level-general-7` is refused even though it is perfectly readable, and
        // the refusal is the whole backward-compatibility story in one line: the
        // general track's rooms are spelled `level-7` and have been since the
        // first one, so admitting a second spelling would split every existing
        // room in the product into two Durable Objects — the people already in it
        // and the people who arrived by the new name. One room, one id.
        if (m[1] === GENERAL_TRACK)
            return null;
        const level = Number(m[2]);
        return level <= LIMITS.levelMax ? raw : null;
    }
    if (TEAM.test(raw))
        return raw;
    if (SUPPORT.test(raw))
        return raw;
    return null;
}
/** The room belonging to a team, or null when that is not a team id. */
export function teamRoom(teamId) {
    return parseRoomId(`team-${teamId}`);
}
/** The team behind a room id, or null when the room is not a team's. */
export function teamOf(room) {
    return TEAM.test(room) ? room.slice('team-'.length) : null;
}
/**
 * Whether a room is a team's, which is the same question as "does it have a
 * door".
 *
 * Takes a `string` rather than a `RoomId` on purpose. Its callers include the
 * two places that keep a private room out of the public presence index
 * (`worker/room.ts` and `worker/presence.ts`), and the second of those is an
 * RPC boundary that receives a bare string. A shape test that could not be
 * asked about a bare string would be a shape test that could not be used where
 * it matters most.
 */
export function isTeamRoom(room) {
    return TEAM.test(room);
}
/** The room belonging to a support thread, or null when that is not a thread id. */
export function supportRoom(threadId) {
    return parseRoomId(`support-${threadId}`);
}
/** The support thread behind a room id, or null when the room is not one's. */
export function supportOf(room) {
    return SUPPORT.test(room) ? room.slice('support-'.length) : null;
}
/** Whether a room is a support conversation's. A `string`, for {@link isTeamRoom}'s reason. */
export function isSupportRoom(room) {
    return SUPPORT.test(room);
}
/**
 * Whether a room is one only some people may enter — and therefore one that
 * must never be counted in the public presence index.
 *
 * This exists because the question was being asked by name. Two places keep
 * private rooms out of `RoomIndex` (`worker/room.ts`'s `tell` and
 * `worker/presence.ts`'s `report`, deliberately duplicated so that the
 * property has two independent guards), and both of them spelled the question
 * `isTeamRoom` — which was correct for exactly as long as teams were the only
 * family with a door. The moment a second one arrived, both guards were
 * silently wrong, and the failure would not have looked like a bug: support
 * conversations would simply have appeared, with their headcounts, in an
 * unauthenticated answer cached at the edge for twenty seconds.
 *
 * Asking the property rather than the family is what makes the *next* private
 * family private by default. A new door added to {@link parseRoomId} and not
 * to this function is a mistake somebody can still make; it is just no longer
 * the default outcome of doing the obvious thing.
 */
export function isPrivateRoom(room) {
    return isTeamRoom(room) || isSupportRoom(room);
}
/** A support thread id on its own, as a route or a stored preference carries it. */
export function cleanSupportId(raw) {
    return typeof raw === 'string' && BARE_ID22.test(raw) ? raw : null;
}
/**
 * A team id on its own, as a route or a stored preference carries it.
 *
 * Same alphabet and length as the half of a room id after `team-`, checked
 * separately so that a route taking a team id does not have to build a room
 * id to find out whether it was given one.
 */
export function cleanTeamId(raw) {
    return typeof raw === 'string' && BARE_ID22.test(raw) ? raw : null;
}
/**
 * A team's name, held to the same rule a person's name is and for the same
 * reasons: it is shown beside other people's words, so it may not carry
 * control characters or the bidi overrides that let text impersonate other
 * text. Longer than a person's name because a team is "Thursday study group"
 * as often as it is "Ada".
 */
export function cleanTeamName(raw) {
    if (typeof raw !== 'string')
        return null;
    // `CONTROL` is the same range `cleanText` strips, and it is stripped here
    // for the same reason: a team name is shown beside other people's words, and
    // the bidi overrides in that range are exactly how text is made to
    // impersonate other text.
    const name = raw.replace(CONTROL, '').replace(/\s+/g, ' ').trim();
    if (name.length < LIMITS.team.name.min || name.length > LIMITS.team.name.max)
        return null;
    // Wider than `NAME_CHARS` by an apostrophe and an ampersand, because a team
    // is "Ada's study group" and "Ops & Infra" as often as it is one word, and
    // narrower than free text in every other respect.
    return /^[\p{L}\p{N} ._'&-]+$/u.test(name) ? name : null;
}
export function numberedRoom(n) {
    return Number.isInteger(n) ? parseRoomId(String(n)) : null;
}
/**
 * The room for one level of one track.
 *
 * `track` is optional and defaults to the general track, which is what makes
 * this a widening rather than a change: every existing caller passes a bare
 * number, every existing caller means the general track, and every existing
 * caller gets back the exact id it got back before. The day a second track
 * ships, the callers that hold a mission pass `mission.track` beside
 * `mission.level` and the rooms separate; until then nothing moves.
 *
 * Null for a track this cannot spell, which is deliberate and is the opposite
 * of what would be convenient. Falling back to the general track for an
 * unrecognised name would put backend's level 0 in general's level 0 room —
 * precisely the collision the track is here to prevent — and it would do it
 * silently. A null propagates into `roomFor` (web/src/lib/chat.ts), which
 * already has a sensible answer for a level with no room of its own: the
 * learner stays in the room they chose.
 */
export function levelRoom(level, track = GENERAL_TRACK) {
    if (!Number.isInteger(level) || level < 0)
        return null;
    return parseRoomId(track === GENERAL_TRACK ? `level-${level}` : `level-${track}-${level}`);
}
/** The level a level room belongs to, or null for a numbered or team room. */
export function roomLevel(id) {
    const m = LEVEL.exec(id);
    return m ? Number(m[2]) : null;
}
/**
 * The track a level room belongs to, or null for a numbered or team room.
 *
 * An unqualified id answers {@link GENERAL_TRACK} rather than null, because
 * that is what it has always meant: `level-7` was the room for level 7 of the
 * only course there was. Null here means "not a level room at all", which is
 * the same thing it means in {@link roomLevel}, and keeping the two functions
 * agreeing about what null means is worth more than reserving it for "no
 * track said".
 */
export function roomTrack(id) {
    const m = LEVEL.exec(id);
    return m ? (m[1] ?? GENERAL_TRACK) : null;
}
/** The number of a numbered room, or null for a level room. */
export function roomNumber(id) {
    return NUMBERED.test(id) ? Number(id) : null;
}
/**
 * "Room 42", "Level 07" — and "الغرفة 42", "المستوى 07".
 *
 * The number keeps its Western digits in both languages, because the level it
 * names is Western everywhere else a learner meets it (docs/i18n.md §4), and
 * the padding is part of the number rather than part of the sentence: a column
 * of room names has to line up in either direction.
 *
 * English by default, so the Worker and every caller that has not been handed
 * a language read what they always read.
 *
 * **A team room has no name here, and that is not an omission.** A team is
 * called what its members called it, which lives in D1 and is not something a
 * pure protocol function can know — and printing the id would be worse than
 * useless, because `team-xTf9…` is 27 characters of nothing in a header that
 * has room for about twelve. So this answers with the generic word and the
 * caller that holds the roster substitutes the real name
 * (`web/src/lib/teams.ts`). A tab that has not loaded its teams yet, and the
 * Worker writing a log line, both get something true and dull rather than
 * something wrong or enormous.
 *
 * **A level room on a named track answers with the track's own word**, as in
 * "backend 07", and that word is the same in both languages. It is the token
 * out of `mission.yaml` rather than prose, for the reason the digits are
 * Western in both: the label has to tell two rooms apart in a header about
 * twelve characters wide, and a course's translated name for a track is
 * catalogue data this module does not hold. A caller that does hold it — the
 * picker, which has the missions — is free to substitute, exactly as the one
 * holding a team's roster substitutes its name. The general track keeps the
 * word "Level" it has always had, because its rooms are the ones that already
 * exist and its levels are the ones already written into screenshots, links
 * and people's memories.
 */
export function roomLabel(id, locale = 'en') {
    if (TEAM.test(id))
        return locale === 'ar' ? 'فريق' : 'Team';
    // Before the level lookup rather than after it, because the fallthrough at
    // the foot of this function prints `String(id)` — which for a support room
    // is thirty characters of base64url in a header with space for about
    // twelve. The generic word, like a team's, and for the same reason: what
    // the conversation is actually about lives in D1 and the caller holding it
    // may substitute.
    if (SUPPORT.test(id))
        return locale === 'ar' ? 'الدعم' : 'Support';
    const level = roomLevel(id);
    const n = level !== null ? String(level).padStart(2, '0') : String(id);
    const track = roomTrack(id);
    if (track !== null && track !== GENERAL_TRACK)
        return `${track} ${n}`;
    if (locale === 'ar')
        return level !== null ? `المستوى ${n}` : `الغرفة ${n}`;
    if (locale === 'de')
        return level !== null ? `Level ${n}` : `Raum ${n}`;
    if (locale === 'ja')
        return level !== null ? `レベル${n}` : `ルーム${n}`;
    return level !== null ? `Level ${n}` : `Room ${n}`;
}
/** The six, in the order a chooser should offer them: money beside money, `other` last. */
export const SUPPORT_TOPICS = ['access', 'billing', 'tasks', 'content', 'bug', 'other'];
/** One of the six, or null. Never a default — a topic nobody chose is a topic nobody meant. */
export function cleanSupportTopic(raw) {
    return typeof raw === 'string' && SUPPORT_TOPICS.includes(raw) ? raw : null;
}
/**
 * The budgets a support conversation is held to.
 *
 * Separate from {@link LIMITS} rather than a branch of it, because {@link
 * LIMITS} is the *room's* budget and every one of these is about the thing
 * around the room: how many conversations, how long a note, how long since an
 * agent was last seen. A support room's messages are bounded by `LIMITS.text`
 * and `LIMITS.rate` like every other room's, and nothing here restates them.
 */
export const SUPPORT_LIMITS = {
    /**
     * Conversations one learner may have open at once.
     *
     * One, and it is the decision the rest of the feature is built on. A person
     * with three open threads is a person whose three questions are being
     * answered by three different agents who cannot see each other's answers,
     * and the product has nowhere to put that. One thread means the second
     * question goes in the conversation that already exists, next to the
     * context that makes it answerable. The database enforces it — a unique
     * partial index, not a check in whichever route happens to be written today.
     */
    openPerLearner: 1,
    /**
     * Conversations one learner may *open* in a rolling day, closed ones
     * counted. Five, which is unreachable by anybody with a question and
     * reachable by somebody opening and closing threads to make a point.
     */
    perDay: 5,
    /** The one line at the top of a thread, in characters after trimming. */
    subject: 80,
    /** An internal note. Long enough for a diagnosis, short enough to read. */
    note: 2000,
    /** A saved answer: its key, its name in the list, and the words it inserts. */
    reply: { slug: 40, title: 60, body: 1500 },
    /** Saved answers one deployment keeps. Past this it is a knowledge base. */
    replies: 60,
    /**
     * How long since an agent last read the queue still counts as being here,
     * in milliseconds.
     *
     * Ninety seconds against a ten-second poll, so an agent stays "here" across
     * a lost packet, a suspended laptop lid and a redeploy, and stops being
     * here about a minute after they actually leave. It is what the learner is
     * told — "somebody is here" — so erring long would be the product lying
     * about a person being available, which is the one error worth avoiding.
     */
    shift: 90_000,
    /** Conversations one queue read returns. */
    queue: 50,
    /** Notes one conversation read returns. */
    notes: 50,
    /**
     * A staff display name.
     *
     * The same bounds as {@link LIMITS.name} and not a coincidence: this string
     * is substituted for the agent's account name at the socket upgrade
     * (`worker/index.ts`), and `decodeIdentity` re-checks it with {@link
     * cleanName} on the way into the room. A display that fails that check does
     * not produce an error — it drops the **whole identity**, and the agent
     * joins their own support conversation as an anonymous listener who cannot
     * post. So it is validated with `cleanName` when it is granted, where
     * somebody is there to be told.
     */
    display: { min: 2, max: 20 },
};
/** The shape of an image id, and the whole of what this package knows about one. */
const IMAGE_ID = /^[0-9a-f]{32}\/[0-9a-f]{64}\.(png|jpg|webp)$/;
/**
 * An image id, or null. Shape only — whether the object exists, whose it is
 * and whether it may be attached to this message are three questions only the
 * Worker can answer, and it asks all three.
 */
export function cleanImageId(raw) {
    return typeof raw === 'string' && IMAGE_ID.test(raw) ? raw : null;
}
/**
 * The path a tab loads one from. Same origin as the app, deliberately: see the
 * header of worker/chat-images.ts for what that buys and what it costs.
 *
 * **A path and not a URL**, and a caller in the tab must put it through
 * `chatUrl` like every other chat request. This package cannot know the
 * origin — a development build points at a Worker on another port through
 * `VITE_CHAT_ORIGIN` — and a bare path used literally would load every picture
 * from the wrong host there, which is a failure that appears only in the one
 * configuration nobody screenshots.
 */
export function imageUrl(id) {
    return `/api/chat/images/${id}`;
}
/* ── Validation ─────────────────────────────────────────────────────────── */
/** Letters and digits from any script, plus space, dot, dash, underscore. */
const NAME_CHARS = /^[\p{L}\p{N} ._-]+$/u;
/**
 * A display name, or null. Trims, collapses runs of whitespace to one space,
 * then checks length and character class. Names are shown as text, so the
 * class is about legibility, not safety.
 */
export function cleanName(raw) {
    if (typeof raw !== 'string')
        return null;
    const name = raw.replace(/\s+/g, ' ').trim();
    if (name.length < LIMITS.name.min || name.length > LIMITS.name.max)
        return null;
    if (!NAME_CHARS.test(name))
        return null;
    return name;
}
/**
 * Everything in C0 and C1 except tab and newline, the line and paragraph
 * separators, and the bidi controls that let text impersonate other text.
 *
 * ## Only half of the bidi range, and which half
 *
 * U+202A-202E are the **embeddings and overrides** — LRE, RLE, PDF, LRO, RLO.
 * An override reverses everything after it until something pops it, and that
 * is the actual spoofing vector: RLO in front of `gpj.exe` makes it read as
 * `exe.jpg`, and a line can be made to say the reverse of what it is. They
 * have no honest use in a message. They are stripped, and this is the range
 * the strip was put here for.
 *
 * U+2066-2069 are the **isolates** — LRI, RLI, FSI, PDI. They are the modern
 * replacement for the above and they do the opposite job: an isolate says
 * "this run has its own direction and it does not reorder the text around
 * it", which is precisely how an English identifier or a path is put inside
 * an Arabic sentence. A room about git is very little else — `--force-with-
 * lease` and `src/cart.js` dropped into Arabic prose — and stripping them
 * turned every one of those messages into the mess the isolate exists to
 * prevent: `src/cart.js` arriving as `cart.js/src`, which reads as the writer
 * being wrong rather than the renderer.
 *
 * The blast radius of an unterminated isolate is one bubble. The element the
 * message is drawn in closes whatever scope the text left open, so at worst a
 * malformed message rearranges itself; it cannot reach the name beside it,
 * the row underneath, or anything outside the panel. That is a rendering bug
 * in one person's own line, not a way to make one person's line look like
 * somebody else's — and it is a far smaller cost than un-isolating every path
 * anybody pastes into the room.
 *
 * Both halves are pinned by a test (packages/chat/test/protocol.test.mjs), so
 * a later tidy-up cannot quietly widen the range back.
 */
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E]/g;
/**
 * One message body, or null. Strips control characters, normalises line
 * endings, allows at most one blank line in a row, trims, and refuses empty
 * or over-long text. Length is counted in UTF-16 units after cleaning, the
 * same way the tab counts it, so the two never disagree at the limit.
 */
export function cleanText(raw) {
    if (typeof raw !== 'string')
        return null;
    const text = raw
        .replace(/\r\n?/g, '\n')
        .replace(CONTROL, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    if (text.length === 0 || text.length > LIMITS.text)
        return null;
    return text;
}
/**
 * The one line at the top of a support conversation, or null.
 *
 * Cleaned like a message and not like a name: it is a sentence somebody
 * types, so it needs the control and bidi stripping {@link cleanText} does,
 * but it is shown in a list of conversations rather than in a thread, so it
 * is one line and it is short. Newlines become spaces rather than being
 * refused — pasting two lines into a subject field is a thing people do, and
 * throwing the whole subject away for it would be a worse answer than
 * flattening it.
 *
 * Optional everywhere it is used: a conversation with no subject is fine, and
 * the topic already says what kind of thing it is.
 */
export function cleanSubject(raw) {
    if (typeof raw !== 'string')
        return null;
    const subject = raw
        // Every line break becomes a space *before* {@link CONTROL} is stripped,
        // and the order is the whole correctness of this function. U+2028 and
        // U+2029 - the line and paragraph separators, which is what a paste out
        // of some editors carries — are inside that class, so stripping first
        // would delete them and join the words either side: `line<LS>and` would
        // arrive as `lineand`. Turning them into spaces first means the promise
        // above is kept for every kind of line break rather than for the two
        // everybody tests with.
        .replace(/[\r\n\u2028\u2029]+/g, ' ')
        .replace(CONTROL, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (subject.length === 0 || subject.length > SUPPORT_LIMITS.subject)
        return null;
    return subject;
}
/** The browser id from `hello`: 8 to 64 URL-safe characters. */
export function cleanId(raw) {
    return typeof raw === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(raw) ? raw : null;
}
/** A rank name: short plain text, or null. Unknown ranks are kept as text
 *  rather than validated against a list the Worker does not have. */
export function cleanRank(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'string')
        return null;
    const rank = raw.replace(/\s+/g, ' ').trim();
    return rank.length > 0 && rank.length <= 32 && NAME_CHARS.test(rank) ? rank : null;
}
export function cleanLevel(raw) {
    if (raw === null || raw === undefined)
        return null;
    return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= LIMITS.levelMax ? raw : null;
}
/**
 * Which objective of a level somebody is on, counting from zero.
 *
 * The ceiling is generous rather than exact because this module does not know
 * how many objectives a level has and must not learn: the missions are the
 * tab's business and the Worker has never read one. What the number is for is
 * "three people here are on objective 2", and an implausible index there
 * costs a wrong word in one sentence.
 */
export function cleanObjective(raw) {
    if (raw === null || raw === undefined)
        return null;
    return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= 63 ? raw : null;
}
/** Stars on a cleared level: 0 to 3, or null. */
export function cleanStars(raw) {
    return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= 3 ? raw : null;
}
/* ── Reactions, mentions and the things they are made of ────────────────── */
/**
 * One tag as it appears on the wire: 32 lowercase hex characters, the shape
 * {@link tagFor} makes. Used wherever a client names somebody — a mention, a
 * block, a ban — because the full tag identifies the actor independently of its display label.
 */
export function cleanTag(raw) {
    return typeof raw === 'string' && /^[0-9a-f]{32}$/.test(raw) ? raw : null;
}
/** Short labels are presentation only; never send one to an identity endpoint. */
export function displayTag(tag) { return tag.slice(0, 4); }
/** Historical messages can still carry the old, ambiguous display tag. */
export function cleanHistoricalTag(raw) {
    return cleanTag(raw) ?? (typeof raw === 'string' && /^[0-9a-f]{4}$/.test(raw) ? raw : null);
}
/** Full mention identities, deduplicated and capped at {@link LIMITS.mention}. */
export function cleanTags(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        const tag = cleanTag(item);
        if (tag && !out.includes(tag))
            out.push(tag);
        if (out.length >= LIMITS.mention)
            break;
    }
    return out;
}
/** Every emoji character the picker offers, for the check below. Built once. */
const REACTABLE = new Set(EMOJI.map((e) => e.c));
/** One of ours as it travels: `:code:`, in the shape custom.ts's own token
 *  regex accepts, anchored because a reaction is one emoji and not a line. */
const CUSTOM_TOKEN = /^:([a-z0-9_+-]{1,24}):$/i;
/**
 * One reaction, canonicalised, or null.
 *
 * The same gate the faces have and for the same reason: a field a browser can
 * fill with anything is a field anyone can use to hang arbitrary text under
 * everybody else's messages. So a reaction is one of the characters in the
 * curated table or one of the shortcodes this product drew for itself, and
 * nothing else — not a sequence of them, not a letter, not a code the roster
 * does not know.
 *
 * The two kinds travel differently, exactly as they do in a message body: a
 * Unicode emoji is its character, and one of ours is its own `:shortcode:`,
 * which is the string the tab looks its drawing up by. What comes back is
 * already the key it buckets under — lowercased for ours, NFC for theirs — so
 * that `:SHIPIT:` and `:shipit:` are one row rather than two, the way
 * `splitCustom` already treats them as one picture.
 */
export function cleanReaction(raw) {
    if (typeof raw !== 'string')
        return null;
    const emoji = raw.normalize('NFC').trim();
    if (emoji.length === 0 || emoji.length > 32)
        return null;
    const ours = CUSTOM_TOKEN.exec(emoji);
    if (ours) {
        const code = ours[1].toLowerCase();
        const found = CUSTOM_BY_CODE.get(code);
        // A sticker is a message, not a face on somebody else's. Refused here
        // rather than merely left out of the reaction palette, because the palette
        // is a suggestion and this is the rule: a tab could send anything, and a
        // sticker in a reaction row would be drawn at word size in a strip built
        // for faces, which is the one place in the panel where a picture the size
        // of a paragraph has nowhere to go.
        return found && found.sticker !== true ? `:${code}:` : null;
    }
    return REACTABLE.has(emoji) ? emoji : null;
}
/**
 * The key one reaction buckets under.
 *
 * {@link cleanReaction} already returns it, so this is for the callers that
 * hold an emoji they know is good — a tab comparing what it drew against what
 * the room sent back — and want the comparison to be the same one the room
 * made. Anything unrecognisable comes back normalised and otherwise untouched,
 * because a key is not a gate and the gate has already been passed.
 */
export function reactionKey(emoji) {
    const key = emoji.normalize('NFC').trim();
    const ours = CUSTOM_TOKEN.exec(key);
    return ours ? `:${ours[1].toLowerCase()}:` : key;
}
/** A search query, or null. Trimmed; runs of space collapsed, because a room
 *  is searched for words rather than for whitespace. */
export function cleanQuery(raw) {
    if (typeof raw !== 'string')
        return null;
    const q = raw.replace(/\s+/g, ' ').trim();
    return q.length >= LIMITS.search.min && q.length <= LIMITS.search.max ? q : null;
}
/**
 * The quoted half of a reply: the parent's text, cut to {@link LIMITS.snippet}
 * at the last word that fits, with an ellipsis when anything was dropped.
 *
 * Newlines become spaces. A quote is one line above a message however many
 * lines the message it quotes was, and a quote that reproduces the shape of a
 * five-line paste is a quote that buries the reply.
 */
export function snippetOf(text) {
    const flat = text.replace(/\s+/g, ' ').trim();
    if (flat.length <= LIMITS.snippet)
        return flat;
    const cut = flat.slice(0, LIMITS.snippet);
    const atWord = cut.lastIndexOf(' ');
    return `${(atWord > LIMITS.snippet / 2 ? cut.slice(0, atWord) : cut).trimEnd()}…`;
}
/**
 * The `@`-runs in a message body, lowercased and without their `@`.
 *
 * A convenience for the composer, which needs to know what is being typed, and
 * a fallback for the renderer. It is deliberately not how a mention is
 * *addressed*: names collide and may hold spaces, so what travels is the tag
 * in `mentions` and this is only what the reader sees highlighted. A run stops
 * at the first character a name may not hold, spaces included, which is why a
 * two-word name is matched by the renderer against the member list rather than
 * by this.
 */
export function mentionNames(text) {
    const out = [];
    for (const m of text.matchAll(/@([\p{L}\p{N}._-]{2,20})/gu)) {
        const name = m[1].toLowerCase();
        if (!out.includes(name))
            out.push(name);
    }
    return out;
}
/**
 * What a `cleared` row reads as, without the name in front of it.
 *
 * The room stores the level and the stars; the sentence is built at each end,
 * because a sentence put on the wire would be a sentence in whatever language
 * the tab that cleared the level happened to be in — and the person reading it
 * is somebody else. The level keeps its Western digits and its padding in both
 * languages, for the reason {@link roomLabel} does.
 */
export function clearedLine(level, locale = 'en') {
    const n = String(level).padStart(2, '0');
    if (locale === 'de')
        return `hat Level ${n} geschafft`;
    if (locale === 'ja')
        return `がレベル${n}をクリア`;
    return locale === 'ar' ? `أنهى المستوى ${n}` : `cleared Level ${n}`;
}
/**
 * What a browser may claim to be wearing: an operator seed, or one of the
 * faces. Null for anything else, and "anything else" is mostly one thing —
 * a URL.
 *
 * The gate the wire depends on, and the reason it is one function rather
 * than two checks at each call site: `avatar` on the wire has three possible
 * shapes and only two of them may arrive from a browser. A URL in a member
 * list is an image request from every reader in the room to a server of the
 * sender's choosing, which is a traffic log of who is in which room; a seed
 * and a face are both drawn from what the client already has.
 */
export function cleanWorn(raw) {
    return cleanSeed(raw) ?? cleanFace(raw);
}
/** An avatar URL as the Worker passes it on from an account: https, and short. Anything else is no picture. */
export function cleanAvatar(raw) {
    if (typeof raw !== 'string' || raw.length === 0 || raw.length > 500)
        return null;
    try {
        return new URL(raw).protocol === 'https:' ? raw : null;
    }
    catch {
        return null;
    }
}
/**
 * The name a room shows for a signed-in person, derived from the name on
 * their account. An account name can be anything — "Dr. Ana María Ruiz-Ortega
 * (she/her)" — and the room's rule is 2 to 20 letters, digits, spaces, dots,
 * dashes and underscores, so the characters outside the rule go, runs of
 * space collapse, and the result is cut at the last word that fits. A name
 * that leaves nothing usable becomes "Learner": a person who signed in is
 * never asked to type a name, so there has to be one.
 */
export function accountName(raw) {
    const fallback = 'Learner';
    if (typeof raw !== 'string')
        return fallback;
    const kept = Array.from(raw.replace(/\s+/g, ' '), (ch) => (NAME_CHARS.test(ch) ? ch : ' '))
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
    if (kept.length <= LIMITS.name.max)
        return kept.length >= LIMITS.name.min ? kept : fallback;
    const cut = kept.slice(0, LIMITS.name.max);
    const atWord = cut.lastIndexOf(' ');
    const short = (atWord >= LIMITS.name.min ? cut.slice(0, atWord) : cut).trim();
    return short.length >= LIMITS.name.min ? short : fallback;
}
/**
 * A client frame, checked field by field. `say` text and `hello`/`me` names
 * are returned already cleaned, so the server never sees a raw string.
 */
export function parseClientMessage(raw) {
    let data;
    if (typeof raw !== 'string')
        return bad('bad-message', 'Text frames only.');
    try {
        data = JSON.parse(raw);
    }
    catch {
        return bad('bad-message', 'Not JSON.');
    }
    if (!isRecord(data) || typeof data.t !== 'string')
        return bad('bad-message', 'Not a chat message.');
    switch (data.t) {
        case 'hello': {
            if (data.v !== PROTOCOL_VERSION)
                return bad('bad-message', `Protocol ${String(data.v)} is not ${PROTOCOL_VERSION}.`);
            const id = cleanId(data.id);
            if (!id)
                return bad('bad-message', 'hello needs an id.');
            // No name at all is a listener. A name that is present and unusable is
            // still an error, so a typo or a stale stored name is never silently
            // demoted to lurking — the tab is told, and can ask for another.
            const absent = data.name === undefined || data.name === null;
            const name = absent ? null : cleanName(data.name);
            if (!absent && !name)
                return bad('bad-name', nameRule());
            return {
                ok: true,
                msg: {
                    t: 'hello',
                    v: PROTOCOL_VERSION,
                    id,
                    name,
                    // A face the table does not know, or a seed this build cannot
                    // read, is dropped rather than refused, the way a rank or a level
                    // is: it costs the room nothing, and a tab one release behind
                    // should not be locked out over a picture.
                    avatar: cleanWorn(data.avatar),
                    rank: cleanRank(data.rank),
                    level: cleanLevel(data.level),
                },
            };
        }
        case 'say': {
            const nonce = cleanNonce(data.nonce);
            const text = cleanText(data.text);
            const image = cleanImageId(data.image);
            // A message needs words or a picture, and does not need both. Before
            // pictures existed this was `if (!text)`, and the sentence it refused
            // with was about the rule for text — which would be a strange thing to
            // read after dropping a photograph, so an empty message is still
            // `bad-text` and a message with a picture in it is simply not empty.
            if (!text && !image)
                return { ok: false, code: 'bad-text', message: textRule(), ...(nonce ? { nonce } : {}) };
            const msg = { t: 'say', text: text ?? '' };
            if (image)
                msg.image = image;
            // A `replyTo` that is not a message id at all is dropped here; one that
            // is a plausible id for a message the room does not have is `bad-target`,
            // and only the room can tell. The split is deliberate: this module knows
            // what an id looks like and the room knows which ones exist.
            const replyTo = messageId(data.replyTo);
            if (replyTo !== null)
                msg.replyTo = replyTo;
            const mentions = cleanTags(data.mentions);
            if (mentions.length > 0)
                msg.mentions = mentions;
            if (nonce)
                msg.nonce = nonce;
            return { ok: true, msg };
        }
        case 'edit': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'edit needs a message id.');
            // The same cleaner `say` uses, so a message cannot be edited into a
            // shape it could never have been sent in. An empty edit is `bad-text`
            // and not a deletion: they are different intentions and a slip of the
            // hand should not be able to turn one into the other.
            const text = cleanText(data.text);
            if (!text)
                return bad('bad-text', textRule());
            return { ok: true, msg: { t: 'edit', id, text } };
        }
        case 'delete': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'delete needs a message id.');
            return { ok: true, msg: { t: 'delete', id } };
        }
        case 'react': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'react needs a message id.');
            const emoji = cleanReaction(data.emoji);
            if (!emoji)
                return bad('bad-reaction', 'That is not one of the faces this room knows.');
            if (typeof data.on !== 'boolean')
                return bad('bad-message', 'react needs `on`.');
            return { ok: true, msg: { t: 'react', id, emoji, on: data.on } };
        }
        case 'pin': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'pin needs a message id.');
            if (typeof data.on !== 'boolean')
                return bad('bad-message', 'pin needs `on`.');
            return { ok: true, msg: { t: 'pin', id, on: data.on } };
        }
        case 'search': {
            const q = cleanQuery(data.q);
            if (!q)
                return bad('bad-search', searchRule());
            return { ok: true, msg: { t: 'search', q } };
        }
        case 'context': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'context needs a message id.');
            return { ok: true, msg: { t: 'context', id } };
        }
        case 'report': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'report needs a message id.');
            const msg = { t: 'report', id };
            // A reason is optional and is never shown to anyone but whoever runs the
            // deployment, so it is cleaned for length and control characters like a
            // message and refused for nothing: a report with an unusable reason is
            // still a report, and losing it would be the worse mistake.
            const reason = cleanText(data.reason);
            if (reason)
                msg.reason = reason;
            return { ok: true, msg };
        }
        case 'event': {
            if (data.kind !== 'cleared')
                return bad('bad-message', `Unknown event "${String(data.kind)}".`);
            const level = cleanLevel(data.level);
            const stars = cleanStars(data.stars);
            if (level === null || stars === null)
                return bad('bad-message', 'A cleared event needs a level and its stars.');
            const nonce = cleanNonce(data.nonce);
            const msg = { t: 'event', kind: 'cleared', level, stars };
            if (nonce)
                msg.nonce = nonce;
            return { ok: true, msg };
        }
        case 'read': {
            const id = messageId(data.id);
            if (id === null)
                return bad('bad-target', 'read needs a message id.');
            return { ok: true, msg: { t: 'read', id } };
        }
        case 'earlier': {
            const before = data.before;
            if (typeof before !== 'number' || !Number.isInteger(before) || before < 1)
                return bad('bad-message', 'earlier needs a message id.');
            return { ok: true, msg: { t: 'earlier', before } };
        }
        case 'me': {
            const msg = { t: 'me' };
            if ('name' in data) {
                const name = cleanName(data.name);
                if (!name)
                    return bad('bad-name', nameRule());
                msg.name = name;
            }
            if ('avatar' in data)
                msg.avatar = cleanWorn(data.avatar);
            if ('rank' in data)
                msg.rank = cleanRank(data.rank);
            if ('level' in data)
                msg.level = cleanLevel(data.level);
            if ('objective' in data)
                msg.objective = cleanObjective(data.objective);
            return { ok: true, msg };
        }
        default:
            return bad('bad-message', `Unknown message type "${data.t}".`);
    }
}
/**
 * The two rules a person can break, in words.
 *
 * They are here rather than in the tab's catalogue because both ends say
 * them: the Worker puts one in an `err` frame and the prompt shows the same
 * sentence before anything is sent, so the panel can never promise something
 * the server then refuses. The Worker passes no language and so writes
 * English on the wire; the tab, which knows what language it is in, asks for
 * its own and shows that instead (web/src/state/useChat.ts).
 */
export function nameRule(locale = 'en') {
    if (locale === 'ar') {
        return `الاسم من ${LIMITS.name.min} إلى ${LIMITS.name.max} حرفًا أو رقمًا، ويقبل المسافة والنقطة والشرطة والشرطة السفلية.`;
    }
    if (locale === 'ja') {
        return `名前は${LIMITS.name.min}〜${LIMITS.name.max}文字で、英字、数字、スペース、ピリオド、ハイフン、アンダースコアが使えます。`;
    }
    if (locale === 'de') {
        return `Ein Name hat ${LIMITS.name.min} bis ${LIMITS.name.max} Buchstaben, Ziffern, Leerzeichen, Punkte, Bindestriche oder Unterstriche.`;
    }
    return `A name is ${LIMITS.name.min} to ${LIMITS.name.max} letters, digits, spaces, dots, dashes or underscores.`;
}
export function textRule(locale = 'en') {
    if (locale === 'ar')
        return `الرسالة من 1 إلى ${LIMITS.text} حرف.`;
    if (locale === 'ja')
        return `メッセージは1〜${LIMITS.text}文字です。`;
    if (locale === 'de')
        return `Eine Nachricht hat 1 bis ${LIMITS.text} Zeichen.`;
    return `A message is 1 to ${LIMITS.text} characters.`;
}
/** The third rule, and the newest: what a search of a room may be. */
export function searchRule(locale = 'en') {
    if (locale === 'ar')
        return `ابحث بكلمة من ${LIMITS.search.min} إلى ${LIMITS.search.max} حرفًا.`;
    if (locale === 'ja')
        return `検索語は${LIMITS.search.min}〜${LIMITS.search.max}文字です。`;
    if (locale === 'de')
        return `Eine Suche hat ${LIMITS.search.min} bis ${LIMITS.search.max} Zeichen.`;
    return `A search is ${LIMITS.search.min} to ${LIMITS.search.max} characters.`;
}
function bad(code, message) {
    return { ok: false, code, message };
}
/** A message id as a client may name one: a positive integer. Whether the
 *  room actually has it is the room's question, not this module's. */
function messageId(raw) {
    return typeof raw === 'number' && Number.isInteger(raw) && raw >= 1 ? raw : null;
}
/** The token a tab uses to recognise its own optimistic row coming back. */
function cleanNonce(raw) {
    return typeof raw === 'string' && raw.length > 0 && raw.length <= 64 ? raw : undefined;
}
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/* ── Identity ───────────────────────────────────────────────────────────── */
/**
 * A 128-bit opaque identity derived with SHA-256. The first four hex
 * characters are the display label; account actions use all 32. Browser
 * ids never leave their socket, and signed-in tags derive from account ids.
 */
export async function tagFor(id) {
    const bytes = new TextEncoder().encode(`engineeros-chat:${id}`);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const view = new Uint8Array(digest);
    return Array.from(view.slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * The tag of a signed-in person: the same function over the account id, in
 * a namespace of its own so that no browser id can produce it. One account
 * is one tag on every device, which is what makes a rename or a second
 * laptop the same person in the member list.
 */
export function accountTag(userId) {
    return tagFor(`account:${userId}`);
}
/* ── Rate limit ─────────────────────────────────────────────────────────── */
/**
 * A sliding window: at most `burst` events in any `windowMs`. Held per
 * socket on the server. Small enough to keep in memory and to lose without
 * consequence when a room hibernates.
 */
export class RateLimiter {
    burst;
    windowMs;
    stamps = [];
    constructor(burst = LIMITS.rate.burst, windowMs = LIMITS.rate.windowMs) {
        this.burst = burst;
        this.windowMs = windowMs;
    }
    /** Record an event at `now` and say whether it was within the limit. */
    allow(now = Date.now()) {
        while (this.stamps.length && now - this.stamps[0] >= this.windowMs)
            this.stamps.shift();
        if (this.stamps.length >= this.burst)
            return false;
        this.stamps.push(now);
        return true;
    }
}
/* ── Members ────────────────────────────────────────────────────────────── */
/**
 * One row per person, however many tabs they have open. The last-seen name,
 * rank and level win, which is how a rename in one tab shows in the list.
 */
export function dedupeMembers(all) {
    const byTag = new Map();
    for (const m of all)
        byTag.set(m.tag, m);
    return Array.from(byTag.values()).sort((a, b) => a.name.localeCompare(b.name) || a.tag.localeCompare(b.tag));
}
//# sourceMappingURL=index.js.map